import {
  autoUpdate,
  flip,
  offset,
  Placement,
  shift,
  useClick,
  useFloating,
  useInteractions,
} from '@floating-ui/react';
import debounce from 'lodash.debounce';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

export interface SelectProps<T> {
  items: T[];
  value: string | null;
  onChange: (id: string, item: T) => void;
  // Accessors
  getId: (item: T) => string;
  getPrimary: (item: T) => string;
  getSecondary?: (item: T) => string | undefined;
  getAvatarText?: (item: T) => string | undefined; // for initials/emoji
  getAvatarUrl?: (item: T) => string | undefined; // for photo URL
  // Search
  searchAccessors?: ((item: T) => string | undefined)[];
  debounceMs?: number;
  // UI
  placeholder?: string;
  emptyText?: string;
  error?: string;
  disabled?: boolean;
  className?: string;
  dropdownDirection?: 'down' | 'up';
}

function getInitials(text?: string): string {
  if (!text) return '';
  return text
    .split(' ')
    .filter(Boolean)
    .map((w) => w[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
}

export function Select<T>(props: SelectProps<T>) {
  const {
    items,
    value,
    onChange,
    getId,
    getPrimary,
    getSecondary,
    getAvatarText,
    getAvatarUrl,
    searchAccessors,
    debounceMs = 200,
    placeholder = 'Выберите...',
    emptyText = 'Ничего не найдено',
    error,
    disabled = false,
    className = '',
    dropdownDirection = 'down',
  } = props;

  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState<number>(-1);

  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  // Convert dropdownDirection to Placement
  const placement: Placement = dropdownDirection === 'up' ? 'top' : 'bottom';

  // Use Floating UI for positioning and interactions
  const { refs, floatingStyles, context } = useFloating({
    open: isOpen,
    onOpenChange: (open) => {
      setIsOpen(open);
      if (!open) {
        setQuery('');
        setHighlightedIndex(-1);
      }
    },
    placement,
    middleware: [
      offset(4), // 4px gap
      flip(), // Flip to opposite side if not enough space
      shift({ padding: 8 }), // Shift to keep in viewport
    ],
    whileElementsMounted: autoUpdate, // Auto-update position on scroll/resize
    strategy: 'fixed', // Use absolute positioning (no portal)
  });

  // Handle click interactions
  const click = useClick(context);

  const { getReferenceProps } = useInteractions([click]);

  // Manual dismiss handling to avoid blocking clicks inside
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const floatingEl = refs.floating.current as HTMLElement | null;
      const referenceEl = refs.reference.current as HTMLElement | null;

      if (
        floatingEl &&
        !floatingEl.contains(target) &&
        referenceEl &&
        !referenceEl.contains(target)
      ) {
        setIsOpen(false);
        setQuery('');
        setHighlightedIndex(-1);
      }
    };

    // Use click event with delay to allow React handlers to run first
    const timeoutId = setTimeout(() => {
      document.addEventListener('click', handleClickOutside);
    }, 0);

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener('click', handleClickOutside);
    };
  }, [isOpen, refs]);

  // Debounce
  const applyDebounce = useMemo(
    () => debounce((q: string) => setDebouncedQuery(q), debounceMs),
    [debounceMs]
  );
  useEffect(() => {
    applyDebounce(query);
    return () => applyDebounce.cancel();
  }, [query, applyDebounce]);

  const selectedItem = useMemo(
    () => items.find((i) => getId(i) === value),
    [items, value, getId]
  );

  const filtered = useMemo(() => {
    const q = debouncedQuery.trim().toLowerCase();
    if (!q) return items;
    const accessors =
      searchAccessors && searchAccessors.length > 0
        ? searchAccessors
        : [getPrimary, getSecondary ?? (() => '')];
    return items.filter((item) =>
      accessors.some((acc) => (acc(item) || '').toLowerCase().includes(q))
    );
  }, [items, debouncedQuery, getPrimary, getSecondary, searchAccessors]);

  // Focus input when open
  useEffect(() => {
    if (isOpen) inputRef.current?.focus();
  }, [isOpen]);

  // Ensure highlighted item in view
  useEffect(() => {
    if (!listRef.current) return;
    const el = listRef.current.querySelector<HTMLElement>(
      `[data-index="${highlightedIndex}"]`
    );
    if (!el) return;
    const { offsetTop, offsetHeight } = el;
    const { scrollTop, clientHeight } = listRef.current;
    if (offsetTop < scrollTop) listRef.current.scrollTop = offsetTop;
    else if (offsetTop + offsetHeight > scrollTop + clientHeight)
      listRef.current.scrollTop = offsetTop + offsetHeight - clientHeight;
  }, [highlightedIndex]);

  const handleSelect = useCallback(
    (item: T) => {
      const id = getId(item);
      onChange(id, item);
      setIsOpen(false);
      setQuery('');
      setHighlightedIndex(-1);
    },
    [getId, onChange]
  );

  const onKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (!isOpen) return;
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setHighlightedIndex((i) => Math.min(i + 1, filtered.length - 1));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setHighlightedIndex((i) => Math.max(i - 1, 0));
      } else if (e.key === 'Enter') {
        e.preventDefault();
        const item = filtered[highlightedIndex];
        if (item) handleSelect(item);
      } else if (e.key === 'Escape') {
        setIsOpen(false);
        setQuery('');
        setHighlightedIndex(-1);
      }
    },
    [isOpen, filtered, highlightedIndex, handleSelect]
  );

  const avatarText =
    selectedItem &&
    (getAvatarText ? getAvatarText(selectedItem) : getPrimary(selectedItem));

  const dropdownContent = isOpen ? (
    <div
      ref={refs.setFloating}
      className="z-[100] bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-hidden"
      style={{
        ...floatingStyles,
        width: refs.reference.current
          ? `${refs.reference.current.getBoundingClientRect().width}px`
          : 'auto',
      }}
    >
      <div className="p-3 border-b border-gray-200">
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setHighlightedIndex(0);
          }}
          onKeyDown={onKeyDown}
          placeholder="Поиск..."
          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
      </div>

      <div ref={listRef} className="max-h-48 overflow-y-auto" role="listbox">
        {filtered.length > 0 ? (
          filtered.map((item, idx) => {
            const id = getId(item);
            const secondary = getSecondary ? getSecondary(item) : undefined;
            const initials = getInitials(
              getAvatarText ? getAvatarText(item) : getPrimary(item)
            );
            return (
              <button
                key={id}
                type="button"
                role="option"
                aria-selected={id === value}
                data-index={idx}
                onMouseEnter={() => setHighlightedIndex(idx)}
                onClick={() => handleSelect(item)}
                className={`w-full px-3 py-3 text-left hover:bg-gray-50 focus:bg-gray-50 focus:outline-none transition-colors ${
                  id === value ? 'bg-blue-50 border-r-2 border-blue-500' : ''
                } ${idx === highlightedIndex ? 'bg-gray-50' : ''}`}
              >
                <div className="flex items-center space-x-3">
                  {id && (
                    <>
                      {getAvatarUrl && getAvatarUrl(item) ? (
                        <img
                          src={getAvatarUrl(item)}
                          alt={getPrimary(item)}
                          className="w-8 h-8 rounded-full object-cover flex-shrink-0"
                        />
                      ) : (
                        <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center flex-shrink-0">
                          <span className="text-white text-xs font-medium">
                            {initials}
                          </span>
                        </div>
                      )}
                    </>
                  )}
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-gray-900 truncate">
                      {getPrimary(item)}
                    </p>
                    {secondary && (
                      <p className="text-xs text-gray-500 truncate">
                        {secondary}
                      </p>
                    )}
                  </div>
                  {id === value && (
                    <svg
                      className="w-4 h-4 text-blue-500"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                  )}
                </div>
              </button>
            );
          })
        ) : (
          <div className="px-3 py-6 text-center">
            <p className="text-sm text-gray-500">{emptyText}</p>
          </div>
        )}
      </div>
    </div>
  ) : null;

  return (
    <div className={`relative ${className}`}>
      <button
        ref={refs.setReference}
        type="button"
        {...getReferenceProps()}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        className={`w-full px-3 py-2 text-left bg-white border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors ${
          error ? 'border-red-500' : 'border-gray-300'
        } ${disabled ? 'bg-gray-50 cursor-not-allowed' : 'hover:border-gray-400'}`}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3 flex-1 min-w-0">
            {selectedItem ? (
              <>
                {getId(selectedItem) && (
                  <>
                    {getAvatarUrl && getAvatarUrl(selectedItem) ? (
                      <img
                        src={getAvatarUrl(selectedItem)}
                        alt={getPrimary(selectedItem)}
                        className="w-8 h-8 rounded-full object-cover flex-shrink-0"
                      />
                    ) : (
                      <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center flex-shrink-0">
                        <span className="text-white text-xs font-medium">
                          {getInitials(avatarText)}
                        </span>
                      </div>
                    )}
                  </>
                )}
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-gray-900 truncate">
                    {getPrimary(selectedItem)}
                  </p>
                  {getSecondary && (
                    <p className="text-xs text-gray-500 truncate">
                      {getSecondary(selectedItem)}
                    </p>
                  )}
                </div>
              </>
            ) : (
              <>
                <div className="w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-gray-400 text-sm">👤</span>
                </div>
                <span className="text-gray-500 text-sm">{placeholder}</span>
              </>
            )}
          </div>
          <svg
            className={`w-5 h-5 text-gray-400 transition-transform ${isOpen ? 'transform rotate-180' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 9l-7 7-7-7"
            />
          </svg>
        </div>
      </button>

      {dropdownContent}

      {error && <p className="mt-1 text-sm text-red-600">{error}</p>}
    </div>
  );
}

export default React.memo(Select) as typeof Select;
